/**
 * Personalisation Form Validation & Dynamic Checkout Enabler
 * Manages required custom line item properties, enables/disables Buy Now & Add to Cart,
 * provides user guidance messages below Buy Now, and ensures line item property data is passed on checkout.
 */
(function () {
  function initPersonalisation() {
    const productForms = document.querySelectorAll('product-form, .product-form');

    productForms.forEach((productFormWrapper) => {
      const form = productFormWrapper.querySelector('form');
      if (!form) return;

      // Find all custom line-item property inputs & selects
      const customInputs = form.querySelectorAll(
        'input[name^="properties["], select[name^="properties["]'
      );

      // If no custom properties on this product, it's a standard non-custom product
      if (!customInputs || customInputs.length === 0) return;

      // Filter out recipient form internal hidden fields if any
      const userCustomInputs = Array.from(customInputs).filter((el) => {
        const name = el.getAttribute('name') || '';
        return !name.includes('__shopify_send_gift_card_to_recipient') && !name.includes('__shopify_offset');
      });

      if (userCustomInputs.length === 0) return;

      const submitBtn = form.querySelector('[type="submit"], .product-form__submit');
      const dynamicCheckoutWrapper = form.querySelector('.shopify-payment-button');

      // Create or locate the guidance notice/message element below the Buy Now button
      let noticeMsg = form.querySelector('.personalisation-helper-msg');
      if (!noticeMsg) {
        noticeMsg = document.createElement('div');
        noticeMsg.className = 'personalisation-helper-msg';
        noticeMsg.setAttribute('role', 'status');
        noticeMsg.innerHTML =
          '<span class="personalisation-msg-icon">✏️</span> <span class="personalisation-msg-text">Please enter your personalisation details above to enable Personalise & Buy Now</span>';

        // Insert directly below the buy buttons / payment button
        const btnContainer = form.querySelector('.product-form__buttons:last-of-type') || form;
        btnContainer.appendChild(noticeMsg);
      }

      function setupPaymentButtonOverlay() {
        const currentDynamicWrapper = form.querySelector('.shopify-payment-button');
        if (!currentDynamicWrapper) return;

        let overlay = currentDynamicWrapper.querySelector('.shopify-payment-button-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'shopify-payment-button-overlay';
          overlay.setAttribute('aria-hidden', 'true');
          overlay.setAttribute('title', 'Please enter your personalisation details first');
          currentDynamicWrapper.appendChild(overlay);

          overlay.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            validateAndHighlight(true);
          });
        }
      }

      function getRequiredFields() {
        const fields = [];

        // Primary personalisation text (e.g. Enter Your Name)
        const nameInputs = form.querySelectorAll(
          '#personalisation-text, input[name="properties[Personalisation Text]"]'
        );
        nameInputs.forEach((input) => {
          if (!fields.includes(input)) fields.push(input);
        });

        // Greeting and Other Greeting logic for snippet 7
        const greetingSelect = form.querySelector('#greeting, select[name="properties[Greeting]"]');
        const otherGreetingInput = form.querySelector('#other-greeting, input[name="properties[Other Greeting]"]');
        if (greetingSelect && otherGreetingInput && greetingSelect.value === 'Other (Enter Below)') {
          if (!fields.includes(otherGreetingInput)) fields.push(otherGreetingInput);
        }

        // Any other input explicitly marked with required
        const otherRequired = form.querySelectorAll('input.required[name^="properties["], select.required[name^="properties["]');
        otherRequired.forEach((input) => {
          // If other greeting is hidden (i.e. greeting is not Other), don't treat it as required
          if (input.id === 'other-greeting' && greetingSelect && greetingSelect.value !== 'Other (Enter Below)') {
            return;
          }
          if (!fields.includes(input)) fields.push(input);
        });

        return fields;
      }

      function isFormValid() {
        const requiredFields = getRequiredFields();
        if (!requiredFields.length) return true;

        for (const field of requiredFields) {
          if (!field.value || field.value.trim() === '') {
            return false;
          }
        }
        return true;
      }

      function updateButtonStates(isValid, showErrors = false) {
        setupPaymentButtonOverlay();

        const paymentBtnContainer = form.querySelector('.shopify-payment-button');
        const overlay = paymentBtnContainer ? paymentBtnContainer.querySelector('.shopify-payment-button-overlay') : null;
        const requiredFields = getRequiredFields();

        if (isValid) {
          // ENABLED STATE
          if (paymentBtnContainer) {
            paymentBtnContainer.classList.remove('is-disabled');
            const innerBtn = paymentBtnContainer.querySelector('button, [role="button"]');
            if (innerBtn) innerBtn.removeAttribute('disabled');
          }
          if (overlay) {
            overlay.style.display = 'none';
          }
          if (submitBtn) {
            submitBtn.classList.remove('is-disabled-custom');
          }

          noticeMsg.classList.remove('is-error');
          noticeMsg.classList.add('is-valid');
          noticeMsg.innerHTML =
            '<span class="personalisation-msg-icon">✓</span> <span class="personalisation-msg-text">Personalisation details added! Ready to checkout.</span>';

          requiredFields.forEach((field) => {
            field.classList.remove('has-error', 'shake-element');
            field.style.borderColor = '';
          });
        } else {
          // DISABLED STATE
          if (paymentBtnContainer) {
            paymentBtnContainer.classList.add('is-disabled');
            const innerBtn = paymentBtnContainer.querySelector('button, [role="button"]');
            if (innerBtn) innerBtn.setAttribute('disabled', 'disabled');
          }
          if (overlay) {
            overlay.style.display = 'block';
          }
          if (submitBtn) {
            submitBtn.classList.add('is-disabled-custom');
          }

          noticeMsg.classList.remove('is-valid');
          if (showErrors) {
            noticeMsg.classList.add('is-error');
            noticeMsg.innerHTML =
              '<span class="personalisation-msg-icon">⚠️</span> <span class="personalisation-msg-text">Please enter your personalisation details above to enable Personalise & Buy Now!</span>';
          } else {
            noticeMsg.classList.remove('is-error');
            noticeMsg.innerHTML =
              '<span class="personalisation-msg-icon">✏️</span> <span class="personalisation-msg-text">Please enter your personalisation details above to enable Personalise & Buy Now</span>';
          }
        }
      }

      function validateAndHighlight(focusField = true) {
        const isValid = isFormValid();
        const requiredFields = getRequiredFields();

        if (!isValid) {
          updateButtonStates(false, true);

          let firstEmpty = null;
          requiredFields.forEach((field) => {
            if (!field.value || field.value.trim() === '') {
              field.classList.add('has-error');
              field.classList.remove('shake-element');
              void field.offsetWidth; // Trigger DOM reflow to restart CSS shake animation
              field.classList.add('shake-element');
              field.style.borderColor = '#d93025';
              if (!firstEmpty) firstEmpty = field;
            } else {
              field.classList.remove('has-error', 'shake-element');
              field.style.borderColor = '';
            }
          });

          if (focusField && firstEmpty) {
            firstEmpty.focus();
            firstEmpty.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return false;
        } else {
          updateButtonStates(true);
          return true;
        }
      }

      // Attach real-time input event listeners
      userCustomInputs.forEach((input) => {
        ['input', 'keyup', 'change', 'paste'].forEach((evtType) => {
          input.addEventListener(evtType, function () {
            const isValid = isFormValid();
            updateButtonStates(isValid, false);
            if (this.value && this.value.trim() !== '') {
              this.classList.remove('has-error', 'shake-element');
              this.style.borderColor = '';
            }
          });
        });
      });

      // Special listener for greeting change
      const greetingSelect = form.querySelector('#greeting');
      if (greetingSelect) {
        greetingSelect.addEventListener('change', function () {
          setTimeout(() => {
            const isValid = isFormValid();
            updateButtonStates(isValid, false);
          }, 60);
        });
      }

      // Intercept Add to Cart click when empty
      if (submitBtn) {
        submitBtn.addEventListener(
          'click',
          function (e) {
            if (!isFormValid()) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              validateAndHighlight(true);
              return false;
            }
          },
          true
        );
      }

      // Intercept Form Submit event
      form.addEventListener(
        'submit',
        function (e) {
          if (!isFormValid()) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            validateAndHighlight(true);
            return false;
          }
        },
        true
      );

      // Initialize state on load
      const initialValid = isFormValid();
      updateButtonStates(initialValid, false);

      // Mutation observer to maintain disabled state when Shopify dynamic buttons asynchronously mount
      const dynamicContainer = form.querySelector('.shopify-payment-button');
      if (dynamicContainer) {
        const observer = new MutationObserver(() => {
          setupPaymentButtonOverlay();
          const valid = isFormValid();
          if (!valid) {
            dynamicContainer.classList.add('is-disabled');
            const btn = dynamicContainer.querySelector('button, [role="button"]');
            if (btn) btn.setAttribute('disabled', 'disabled');
          }
        });
        observer.observe(dynamicContainer, { childList: true, subtree: true });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPersonalisation);
  } else {
    initPersonalisation();
  }

  // Support Shopify section updates / theme editor events
  document.addEventListener('shopify:section:load', initPersonalisation);
  document.addEventListener('variant:change', initPersonalisation);
})();
